
import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';

export const Gallery = ({ isOpen, onClose, service, images = [] }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const imageRef = useRef(null);

  // Default gallery images as fallback
  const defaultGalleryImages = [
    '/images/events/event_gallery_1.jpg',
    '/images/events/WhatsApp Image 2025-08-20 at 11.36.44_cd56e133_1756930965318.jpg',
    '/images/events/WhatsApp Image 2025-08-20 at 11.36.45_e02f43ed_1756930852815.jpg',
    '/images/events/WhatsApp Image 2025-09-03 at 12.17.47_898c7848_1757073296179.jpg',
    '/images/events/WhatsApp Image 2025-09-03 at 12.29.01_69f7cb7d_1757073129682.jpg',
    '/images/events/WhatsApp Image 2025-09-03 at 12.31.34_0a405365_1756931201118.jpg',
    '/images/events/WhatsApp Image 2025-09-03 at 12.31.34_d115d7bb_1756931210168.jpg',
    '/images/events/image11.png',
    '/images/events/Axis Bhajji Harsha_1756933332556.png'
  ];

  // Process display images - extract URLs from objects if needed
  const displayImages = React.useMemo(() => {
    if (!images || images.length === 0) {
      return defaultGalleryImages;
    }
    
    // Convert image objects to URL strings if necessary
    return images.map(img => {
      if (typeof img === 'string') {
        return img;
      }
      if (img && img.url) {
        return img.url;
      }
      return null;
    }).filter(url => url !== null);
  }, [images]);

  // Reset when gallery opens or images change
  useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(0);
      setImageLoadErrors({});
    }
  }, [isOpen, images]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextImage();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyPress);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose]);

  // Early return if not open
  if (!isOpen) {
    return null;
  }

  // Check if we have valid images
  if (!displayImages || displayImages.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center" onClick={onClose}>
        <div className="text-center text-white">
          <ImageIcon className="w-16 h-16 mx-auto mb-4" />
          <p>No images available</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-white/20 rounded">Close</button>
        </div>
      </div>
    );
  }

  // Navigation functions
  const nextImage = () => {
    if (displayImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
    }
  };

  const prevImage = () => {
    if (displayImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    }
  };

  const handleImageError = (index) => {
    console.error(`Failed to load image at index ${index}:`, displayImages[index]);
    setImageLoadErrors(prev => ({ ...prev, [index]: true }));
  };

  // Touch handlers for mobile swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && displayImages.length > 1) {
      nextImage();
    }
    if (isRightSwipe && displayImages.length > 1) {
      prevImage();
    }
  };

  // Get current image safely - NOW properly declared after displayImages
  const currentImage = displayImages[currentImageIndex];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full max-w-7xl mx-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-black/80 p-3 sm:p-4 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
              {service && service.icon && (
                <service.icon className="w-full h-full text-white stroke-1" />
              )}
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold truncate">
                {service ? `${service.title} Gallery` : 'Gallery'}
              </h2>
              <p className="text-xs sm:text-sm text-white/80">
                {currentImageIndex + 1} of {displayImages.length}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 sm:p-2 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
            aria-label="Close gallery"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Main Image Display */}
        <div className="flex-1 flex items-center justify-center p-2 sm:p-4 min-h-0">
          <div 
            className="relative w-full h-full max-w-5xl max-h-full"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {currentImage && !imageLoadErrors[currentImageIndex] ? (
              <img
                ref={imageRef}
                src={currentImage}
                alt={`Gallery image ${currentImageIndex + 1}`}
                className="w-full h-full object-contain rounded-lg select-none"
                onError={() => handleImageError(currentImageIndex)}
                draggable={false}
                onLoad={() => console.log(`Successfully loaded image: ${currentImage}`)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-lg">
                <div className="text-center text-white/70">
                  <ImageIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base font-medium mb-1 sm:mb-2">Image not available</p>
                  <p className="text-xs sm:text-sm text-white/50 px-4">
                    {currentImage ? `Failed to load: ${currentImage}` : `Gallery image ${currentImageIndex + 1}`}
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Arrows */}
            {displayImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 sm:p-4 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors touch-manipulation z-10"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 sm:p-4 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors touch-manipulation z-10"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Thumbnails */}
        {displayImages.length > 1 && (
          <div className="bg-black/80 p-2 sm:p-4 flex-shrink-0">
            <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2">
              {displayImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded border-2 overflow-hidden transition-all touch-manipulation ${
                    index === currentImageIndex
                      ? 'border-white ring-2 ring-white/50'
                      : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  {image && !imageLoadErrors[index] ? (
                    <img
                      src={image}
                      alt={`Gallery thumbnail ${index + 1}`}
                      className="w-full h-full object-cover select-none"
                      onError={() => handleImageError(index)}
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 sm:w-6 sm:h-6 text-white/50" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Swipe indicator for mobile */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/50 text-xs sm:hidden pointer-events-none">
          Swipe left or right to navigate • Use arrow keys on desktop
        </div>
      </div>
    </div>
  );
};
